import { useMemo, useState } from 'react';

import {
  Skeleton,
  Chip,
  Icons,
  Tooltip,
  EntityAction,
  EntityActions,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';

import { Money } from 'components/money';
import { NodeCost } from 'features/cluster/nodes/_components/NodeCost';
import { NodePriceAdjustmentsTooltip } from 'features/cluster/nodes/_components/NodePriceAdjustmentsTooltip';
import { COST_PERIODS } from 'features/cluster/nodes/_constants/cost';
import { CostPeriod } from 'features/cluster/nodes/_types/cost';
import {
  calculateBaseCostPerCPU,
  calculateCostPerCPU,
  calculateInstanceCost,
  calculateInstanceRegularCost,
} from 'features/cluster/nodes/_utils/nodeCost';
import { PreparedNode } from 'features/cluster/nodes/NodeList/types';

import { Section } from './Section';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type Props = {
  node: PreparedNode;
};

const SubSection = ({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <Stack gap={4}>
      <Typography variant="L10R" color="textSecondary">
        {title}
      </Typography>
      {children}
    </Stack>
  );
};

const EmptyValuePlaceholder = () => (
  <Typography variant="h6" color="textSecondary">
    --
  </Typography>
);

const AdjustedPriceTooltip = ({
  basePrice,
  adjustedPrice,
  costPeriod,
  children,
  disabled,
  testId,
}: {
  basePrice: number;
  adjustedPrice: number;
  costPeriod: CostPeriod;
  children: React.ReactElement;
  disabled: boolean;
  testId?: string;
}) => {
  const suffix = `/${costPeriod[0]}`;
  const precision = 10;
  const fixed = 10;

  return (
    <Tooltip
      disableFocusListener={disabled}
      disableHoverListener={disabled}
      disableTouchListener={disabled}
      arrow
      placement="top"
      testId={testId}
      title={
        <Stack>
          <Stack direction="row" gap={5}>
            <Typography variant="P12R">Base price: </Typography>
            <Typography variant="P12M">
              <Money
                amount={Number(basePrice)}
                suffix={suffix}
                precision={precision}
                fixed={fixed}
              />
            </Typography>
          </Stack>
          <Stack direction="row" gap={5}>
            <Typography variant="P12R">Adjusted price: </Typography>
            <Typography variant="P12M">
              <Money
                amount={Number(adjustedPrice)}
                suffix={suffix}
                precision={precision}
                fixed={fixed}
              />
            </Typography>
          </Stack>
        </Stack>
      }
    >
      {/*
        Box wrapper is needed for the tooltip to work.
        Otherwise, we have to pass ref a few layers deep.
      */}
      <Box>{children}</Box>
    </Tooltip>
  );
};

export const CostSection = ({ node }: Props) => {
  const [costPeriod, setCostPeriod] = useState<CostPeriod>('hourly');
  const { isCastAnywhere } = useCluster();

  const instanceCost = useMemo(
    () => calculateInstanceCost({ node, costPeriod }),
    [node, costPeriod]
  );
  const costPerCPU = useMemo(
    () => calculateCostPerCPU({ node, costPeriod, isCastAnywhere }),
    [node, costPeriod, isCastAnywhere]
  );
  const baseInstanceCost = useMemo(
    () => calculateInstanceRegularCost({ node, costPeriod }),
    [node, costPeriod]
  );
  const baseCpuCost = useMemo(
    () => calculateBaseCostPerCPU({ node, costPeriod, isCastAnywhere }),
    [node, costPeriod, isCastAnywhere]
  );
  const hasDiscounts = !!node.pricingInfo?.discounts?.length;

  const periodMenu = (
    <EntityActions
      triggerButtonProps={{ size: 'sm', children: <Icons.DotsThree /> }}
    >
      {COST_PERIODS.map((item) => (
        <EntityAction
          key={item}
          selected={costPeriod === item}
          onClick={() => setCostPeriod(item)}
        >
          <Typography
            variant="P12R"
            color={costPeriod === item ? 'blue.700' : 'inherit'}
          >
            {item}{' '}
            <Typography
              variant="inherit"
              color="textSecondary"
              component="span"
            >
              /{item[0]}
            </Typography>
          </Typography>
        </EntityAction>
      ))}
    </EntityActions>
  );

  return (
    <Section
      title="Cost"
      headerAction={periodMenu}
      testId="node-details-cost-section"
    >
      <Stack direction="row" justifyContent="space-between">
        <SubSection title="Instance cost">
          <AdjustedPriceTooltip
            basePrice={baseInstanceCost}
            adjustedPrice={instanceCost}
            disabled={!hasDiscounts}
            costPeriod={costPeriod}
            testId="node-details-instance-cost-tooltip"
          >
            <NodeCost
              costPeriod={costPeriod}
              price={instanceCost}
              emptyValuePlaceholder={<EmptyValuePlaceholder />}
              primaryTextProps={{ variant: 'h6' }}
              suffixTextProps={{ variant: 'P12R' }}
              showUnroundedPriceTooltip={!hasDiscounts}
              testId="node-details-instance-cost"
            />
          </AdjustedPriceTooltip>
        </SubSection>
        <SubSection title="Cost per CPU">
          <AdjustedPriceTooltip
            basePrice={baseCpuCost}
            adjustedPrice={costPerCPU}
            disabled={!hasDiscounts}
            costPeriod={costPeriod}
            testId="node-details-cost-per-cpu-tooltip"
          >
            <NodeCost
              costPeriod={costPeriod}
              price={costPerCPU}
              emptyValuePlaceholder={<EmptyValuePlaceholder />}
              primaryTextProps={{ variant: 'h6' }}
              suffixTextProps={{ variant: 'P12R' }}
              showUnroundedPriceTooltip={!hasDiscounts}
              testId="node-details-cost-per-cpu"
            />
          </AdjustedPriceTooltip>
        </SubSection>
        <SubSection title="Adjustments">
          {hasDiscounts ? (
            <NodePriceAdjustmentsTooltip
              discounts={node.pricingInfo?.discounts || []}
              testId="node-price-adjustments-tooltip"
            >
              <Chip
                size="sm"
                sx={{
                  backgroundColor: 'green.600',
                  color: '#fff',
                  borderRadius: '16px',
                }}
                testId="node-price-adjustments-chip"
              >
                <Icons.Percent size="12px" weight="bold" />
              </Chip>
            </NodePriceAdjustmentsTooltip>
          ) : (
            <EmptyValuePlaceholder />
          )}
        </SubSection>
      </Stack>
    </Section>
  );
};

const CostSectionSkeleton = () => {
  return (
    <Section title="Cost">
      <Stack direction="row" justifyContent="space-between">
        <SubSection title={<Skeleton variant="text" width={100} />}>
          <Typography variant="h6">
            <Skeleton variant="text" width={100} />
          </Typography>
        </SubSection>
        <SubSection title={<Skeleton variant="text" width={100} />}>
          <Typography variant="h6">
            <Skeleton variant="text" width={100} />
          </Typography>
        </SubSection>
        <SubSection title={<Skeleton variant="text" width={100} />}>
          <Typography variant="h6">
            <Skeleton variant="text" width={100} />
          </Typography>
        </SubSection>
      </Stack>
    </Section>
  );
};

CostSection.Skeleton = CostSectionSkeleton;
